import { useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import useBodyClass from '../../common/hooks/useBodyClassName';
import ratesFixed from '../../images/newDesign/ratesFixed.svg';
import ratesFlexible from '../../images/newDesign/ratesFlexible.svg';
import { StyledUpdateRates } from './UpdateRates.styled';
import UpdateFixedRates from './UpdateFixedRates';
import UpdateFlexibleRates from './UpdateFlexibleRates';
import {UpdateRatesData} from "./UpdateRatesData";
import {useAccessToken} from "../../common/hooks/useAccessToken";
import {constants} from "../../common/constants/constants";
import {hideLoader, showLoader} from "../../store/common/slice";
import ErrorsHandler from "../../common/errors/handleErrors";
import {NotificationManager} from "react-notifications";
import { clearBuildingPaymentsCache } from "../../store/buildings/slice";

function UpdateRates({match}) {

    const { t } = useTranslation();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const [currentRates, setCurrentRates] = useState('flexible');
    const client = useAccessToken();
    const dispatch = useDispatch();
    let currentBuildingId = match.params.buildingID;
    const apartments = useSelector(state => state.buildings.managerData?.tenantsAsRows ?? []);
    const buildingId = useSelector(state => state.buildings.managerData?.buildingID);

    useBodyClass('report');

    async function handleFlexibleRatesSubmit(values) {
        const apartmentNumbers = Object.keys(values.amounts);
        await applySettings(values.startDate, values.endDate, apartmentNumbers, values.amounts, values.addToExistingRate);
    }

    async function handleFixedRatesSubmit(values) {
        const apartmentNumbers = apartments.map(a => a.ApartmentNumber);
        const amounts = Object.fromEntries(apartmentNumbers.map(aptNum => ([aptNum, Number(values.amount)])));
        await applySettings(values.startDate, values.endDate, apartmentNumbers, amounts, values.addToExistingRate);
    }

    async function applySettings(startDate, endDate, apartmentNumbers, amounts, addToExistingRate) {
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const dataSet = [];
        dispatch(showLoader());
        try {
            for (let year = startYear, max = endYear + 1; year < max; year++) {
                const data = new UpdateRatesData();

                const existingRates = await loadExistingRates(buildingId, year);
                data.setRates(existingRates);

                const userRates = fillUserRates(year, startDate, endDate, amounts);
                data.setFeeDifferences(userRates);
                if (addToExistingRate) {
                    data.addRates(userRates);
                } else {
                    data.setRates(userRates);
                }
                dataSet.push([year, data]);
            }
            for (const [year, data] of dataSet) {
                await client.managers.payments.uploadPaymentSettings(buildingId, year, data);
            }
            await sendInternalMail(amounts, addToExistingRate);
            NotificationManager.success(t('payment_settings.updatedMonthlySuccessfully'));
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
        } finally {
            dispatch(hideLoader());
            dispatch(clearBuildingPaymentsCache({buildingId: currentBuildingId}))
        }
    }

    /** @returns {Promise<RatesData>} */
    async function loadExistingRates(buildingId, year) {
        const monthlySettings = await client.managers.paymentSettings.getMonthlySettings(buildingId, year);
        const existingRates = monthlySettings[year]
            .map(as => ({ ...as, apartmentNumber: as.apartmentName }))
            .reduce((acc, s) => Object.assign(acc, { [s.apartmentName]: s }), {});
        return existingRates;
    }

    /**
     * @param {number} year
     * @param {Date} startDate
     * @param {Date} endDate
     * @param {{ [apartmentNumber: string]: number }} amounts
     * @returns {RatesData}
     * */
    function fillUserRates(year, startDate, endDate, amounts) {
        const apartmentNumbers = Object.keys(amounts);
        const result = Object.fromEntries(apartmentNumbers.map(aptNum => ([aptNum, {}])));
        for (const month of getMonthIterator(year, startDate, endDate)) {
            for (const apartmentNumber of apartmentNumbers) {
                result[apartmentNumber][month] = amounts[apartmentNumber];
            }
        }
        return result;
    }

    /**
     * @param {number} year
     * @param {Date} startDate
     * @param {Date} endDate
     * @return {IterableIterator<string>}
     * */
    function *getMonthIterator(year, startDate, endDate) {
        const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
        const incrementMonth = (d) => d.setMonth(d.getMonth() + 1, 1);

        const iterableDate = new Date(startDate.valueOf());
        const rightBoundaryDate = new Date(endDate.valueOf());
        incrementMonth(rightBoundaryDate);

        while (!sameMonth(iterableDate, rightBoundaryDate)) {
            if (iterableDate.getFullYear() === year) {
                yield constants.MonthsToText[iterableDate.getMonth() + 1];
            }
            incrementMonth(iterableDate);
        }
    }

    const sendInternalMail = async (amounts, addToExistingRate) => {
        const query = `?buildingID=${buildingId}`;
        const apartments = Object.keys(amounts).map(key=>{ return `Apartment ${key}, Amount: ${amounts[key]} <br>`}).join('');
        const params = {
            sendTo: ["talv@bllink.co"],
            title:  'Manager Updated Fees - building: <buildingAddress>',
            content: 'Manager Updated Fees <br>' +
                `BuildingID: ${buildingId},  Address: <buildingAddress> <br>` +
                `Added To Existing Rate: ${addToExistingRate} <br>` +
                `<b><u>Apartments:</u></b> <br>` +
                `<p>${apartments}</p>` +
                'User: <userID>'
        };
        await client.tenants.utils.postInternalEmail(constants.backendEndpoints.GENERIC_INTERNAL_EMAIL_ENDPOINT ,query , params);
    }

    return (
        <StyledUpdateRates ltr={ltr}>
            <h1 className='page-title text-only'>
                {t('payment_settings.updateMonthlyRates')}
            </h1>

            <div className='switch-btns-wrapper'>
                <div className={`switch-btn ${currentRates === 'flexible' ? 'active' : ''}`} onClick={() => setCurrentRates('flexible')}>
                    <img src={ratesFlexible} />
                    {t('payment_settings.variableAmount')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>

                <div className={`switch-btn ${currentRates === 'fixed' ? 'active' : ''}`} onClick={() => setCurrentRates('fixed')}>
                    <img src={ratesFixed} />
                    {t('payment_settings.fixedAmount')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
            </div>

            <div className='update-wrapper'>
                {currentRates === 'fixed' && (
                    <UpdateFixedRates
                        onSubmit={handleFixedRatesSubmit}
                    />
                )}
                {currentRates === 'flexible' && (
                    <UpdateFlexibleRates
                        apartments={apartments}
                        onSubmit={handleFlexibleRatesSubmit}
                        client={client}
                        buildingID={buildingId}
                    />
                )}
            </div>
        </StyledUpdateRates>
    )
}

export default UpdateRates;
